import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  loadGoogleMaps, getGoogleMapsMapId, getMapColorScheme, didGoogleMapsAuthFail,
} from '../../../utils/googleMapsLoader';

// One marker per city, sized by how many brokers operate there and labelled with
// the count. Clicking a city selects it (the side panel then lists its brokers as
// cards) — this avoids the unreadable pile-up of per-broker pins on a city centre.
export interface CityCluster {
  cityId: string;
  name: string | null;
  lat: number;
  lng: number;
  count: number;
  totalListings: number;
}

interface BrokerMapViewProps {
  clusters: CityCluster[];
  color: string;
  selectedCity: string | null;
  onSelectCity: (cityId: string | null) => void;
}

const KSA_CENTER = { lat: 24.0, lng: 45.0 };

function clusterEl(count: number, max: number, color: string, selected: boolean): HTMLElement {
  const size = 30 + Math.sqrt(count / Math.max(1, max)) * 26; // ~30px … ~56px
  const d = document.createElement('div');
  d.style.cssText = `width:${size}px;height:${size}px;border-radius:9999px;`
    + `background:${color};border:3px solid #ffffff;`
    + `box-shadow:0 4px 12px rgba(15,23,42,${selected ? 0.5 : 0.3});`
    + `display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;`
    + `font-size:${Math.max(12, Math.min(18, Math.round(size * 0.4)))}px;cursor:pointer;`
    + `transition:transform .12s ease;`
    + (selected ? 'transform:scale(1.14);outline:3px solid rgba(0,77,64,0.35);outline-offset:2px;' : '');
  d.textContent = String(count);
  return d;
}

export default function BrokerMapView({ clusters, color, selectedCity, onSelectCity }: BrokerMapViewProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapObj = useRef<google.maps.Map | null>(null);
  const markers = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const { i18n } = useTranslation();
  const [authFail, setAuthFail] = useState(false);
  const [ready, setReady] = useState(false);

  // Init map once.
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !mapRef.current) return;
      if (didGoogleMapsAuthFail()) { setAuthFail(true); return; }
      mapObj.current = new google.maps.Map(mapRef.current, {
        mapId: getGoogleMapsMapId(),
        center: KSA_CENTER,
        zoom: 6,
        colorScheme: getMapColorScheme(),
      });
      setReady(true);
    }).catch(() => setAuthFail(true));
    return () => { cancelled = true; };
  }, []);

  // Render one marker per city whenever the data / selection / colour changes.
  useEffect(() => {
    const map = mapObj.current;
    if (!map || !ready) return;
    // A key refused after the map was created (RefererNotAllowedMapError, quota)
    // makes every marker call throw; without this guard the throw reached React
    // and the whole page fell to its error screen. Show the calm fallback instead.
    try {
      markers.current.forEach((m) => { m.map = null; });
      markers.current = [];

      const max = Math.max(1, ...clusters.map((c) => c.count));
      for (const c of clusters) {
        const selected = c.cityId === selectedCity;
        const el = clusterEl(c.count, max, color, selected);
        const m = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: c.lat, lng: c.lng },
          map,
          content: el,
          zIndex: selected ? 1000 : 1,
          title: `${c.name ?? ''} - ${c.count} ${c.count === 1 ? 'broker' : 'brokers'}`,
        });
        m.addListener('gmp-click', () => onSelectCity(selected ? null : c.cityId));
        markers.current.push(m);
      }
    } catch (error) {
      console.warn('[BrokerMapView] map markers unavailable:', error);
      setAuthFail(true);
    }
  }, [clusters, color, selectedCity, ready, onSelectCity]);

  if (authFail) {
    return (
      // Visitors see a plain sentence; the cause (the Maps key refusing this
      // address) is in the console warning above, for whoever runs the site.
      <div className="flex h-full w-full items-center justify-center bg-[var(--pv-color-surface-sunken)] p-6 text-center text-[0.9375rem] text-[var(--pv-color-text-muted)]">
        {i18n.language === 'ar' ? 'تعذّر تحميل الخريطة حالياً. حاول مرة أخرى لاحقاً.' : 'The map could not load right now. Please try again later.'}
      </div>
    );
  }
  return <div ref={mapRef} className="h-full w-full" />;
}
